import { Button, Card, CardContent, Divider, Typography } from '@mui/material'
import React, { useEffect, useRef } from 'react'
import { Helmet } from '@dr.pogodin/react-helmet'
import UserApi from '../../../api/UserApi'
import useAuth from '../../../hooks/useAuth'
import NewUserDialog from './NewUserDialog'
import UsersGrid from '../../../components/entities/UsersGrid'
import { Stack } from '@mui/system'

import { applyFilter } from './filterUsers'
import UsersFilter from './UsersFilter'
import { UserEntity } from '@finance-lab/utils/types'

const userApi = new UserApi()
const sortByEmail = (a, b) => (a.email > b.email ? 1 : -1)
const sortByName = (a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : sortByEmail(a, b))

const displayCount = (users: UserEntity[]) => {
    const disabledUsers = users.filter((user) => user.disabled).length
    const enabledUsers = users.length - disabledUsers
    return `${enabledUsers} aktiverade, ${disabledUsers} inaktiverade`
}

function AdminUsersView() {
    const { isAuthenticated } = useAuth()
    const [users, setUsers] = React.useState<Array<UserEntity> | null>(null)
    const [filteredUsers, setFilteredUsers] = React.useState<Array<UserEntity> | null>(null)
    const [filterState, setFilterState] = React.useState({ enabledDisabled: 'enabled', userCategory: 'standard' })
    const [open, setOpen] = React.useState(false)
    const triggerButtonRef = useRef<HTMLButtonElement>(null) // Ref for the trigger button

    useEffect(() => {
        async function fetchUsers() {
            if (!isAuthenticated) return
            let fetchedUsers = await userApi.adminFindAllUsers()
            setUsers(fetchedUsers)
            fetchedUsers = applyFilter(fetchedUsers, filterState)
            fetchedUsers = fetchedUsers.sort(sortByName)
            setFilteredUsers(fetchedUsers)
        }
        fetchUsers()
    }, [filterState, isAuthenticated])

    // Effect to update the page title with the number of users
    useEffect(() => {
        if (users) {
            const numberOfUsers = users.length
            document.title = `Användare (${numberOfUsers})`
        }
    }, [users])

    useEffect(() => {
        if (users) {
            applyFilter(users, filterState)
        }
    }, [filterState, users])

    const handleOpenDialog = () => {
        setOpen(true)
    }

    const handleCloseDialog = () => {
        setOpen(false)
        if (triggerButtonRef.current) {
            triggerButtonRef.current.focus() // Return focus to the button that opened the dialog
        }
    }

    const handleCreateUser = async ({ customer, ...formData }) => {
        try {
            await userApi.createUser({ customerId: customer?.id || null, ...formData })
            const updatedUsers = await userApi.adminFindAllUsers()
            const _users = updatedUsers?.sort(sortByName)
            setUsers(_users)
            handleCloseDialog()
        } catch (error) {
            console.error('Failed to create user:', error)
            throw error
        }
    }

    return (
        <React.Fragment>
            <Helmet>
                <title>Användare</title>
            </Helmet>
            <Typography
                variant='h3'
                gutterBottom
                sx={{
                    mb: 4,
                    display: 'block',
                }}
            >
                Användare ({users ? displayCount(users) : '...'})
            </Typography>
            <Stack
                direction='row'
                spacing={2}
                sx={{
                    mb: 4,
                }}
            >
                <UsersFilter
                    users={users || []}
                    setUsers={setFilteredUsers}
                    filterState={filterState}
                    setFilterState={setFilterState}
                />
                <Divider orientation='vertical' flexItem />
                <Button
                    variant='contained'
                    color='primary'
                    onClick={handleOpenDialog}
                    ref={triggerButtonRef} // Assign ref to the trigger button
                >
                    Skapa ny användare
                </Button>
            </Stack>
            <Divider />
            <Card mb={6}>
                <CardContent>
                    <UsersGrid users={filteredUsers} setUsers={setUsers} />
                </CardContent>
            </Card>
            <NewUserDialog open={open} onClose={handleCloseDialog} onSubmit={handleCreateUser} />
        </React.Fragment>
    )
}

export default AdminUsersView
